import React, { createContext, useContext, useState } from 'react'

/**
 * The job the CURRENT PAGE is about, published to the header.
 *
 * The header cannot derive this itself. `account.job` is whatever the user
 * last picked in the picker, and the dashboard pages deliberately do not
 * change it — a dashboard reads its own `dashboard.job`, which #961 fixed at
 * creation. On a dashboard the two can disagree, so a header pill fed from
 * the account session would confidently show the wrong job.
 *
 * Pages that own a job set it here; the header shows it read-only. A page
 * with no job of its own (a legacy pre-#961 dashboard, of which there are
 * still plenty) sets '' and the header shows nothing rather than guessing.
 */
interface PageJobContextValue {
  /** Job NAME the current page is scoped to, or '' when it has none. */
  pageJob: string
  /**
   * The page's whole job set (#1162 multi-job dashboards; #1229 C1). More
   * than one entry and the header pill reads "Multi" — the job list lives
   * in the dashboard's Settings. pageJob is always pageJobs[0].
   */
  pageJobs: string[]
  setPageJob: (job: string) => void
  setPageJobs: (jobs: string[]) => void
}

const PageJobContext = createContext<PageJobContextValue>({
  pageJob: '',
  pageJobs: [],
  setPageJob: () => {},
  setPageJobs: () => {},
})

export const usePageJob = () => useContext(PageJobContext)

export const PageJobProvider: React.FC<{
  value: PageJobContextValue
  children: React.ReactNode
}> = ({ value, children }) => (
  <PageJobContext.Provider value={value}>{children}</PageJobContext.Provider>
)

/** State for the provider, owned by whoever renders both the header and the page. */
export function usePageJobState(): PageJobContextValue {
  const [pageJobs, setPageJobs] = useState<string[]>([])
  return {
    pageJob: pageJobs[0] || '',
    pageJobs,
    setPageJob: (job: string) => setPageJobs(job ? [job] : []),
    setPageJobs,
  }
}
